/**
 * PAYG Withholding Calculator based on ATO formulas
 * References:
 * - https://www.ato.gov.au/tax-rates-and-codes/payg-withholding-schedule-1-statement-of-formulas-for-calculating-amounts-to-be-withheld/
 * - https://www.ato.gov.au/tax-rates-and-codes/tax-table-monthly/
 * - https://www.ato.gov.au/tax-rates-and-codes/tax-table-weekly/
 * - https://www.ato.gov.au/tax-rates-and-codes/tax-table-daily-and-casual-workers/
 */

import { TaxYearType, FrequencyType } from './types';
import { round } from './utils';

// 2024-25 Withholding formulas for resident individuals claiming tax-free threshold
// Based on ATO formulas: https://www.ato.gov.au/tax-rates-and-codes/tax-table-weekly/
// Weekly earnings
const WITHHOLDING_WEEKLY_RESIDENT_TFT = [
  { min: 0, max: 350, a: 0, b: 0 },
  { min: 350, max: 438, a: 0.19, b: 66.5 },
  { min: 438, max: 548, a: 0.29, b: 110.22 },
  { min: 548, max: 721, a: 0.2100, b: 66.16 },
  { min: 721, max: 865, a: 0.2190, b: 72.64 },
  { min: 865, max: 1282, a: 0.3477, b: 183.97 },
  { min: 1282, max: 2307, a: 0.345, b: 180 },
  { min: 2307, max: 3461, a: 0.39, b: 283.65 },
  { min: 3461, max: 3654, a: 0.47, b: 661.89 },
  { min: 3654, max: Infinity, a: 0.45, b: 588.93 },
];

// 2024-25 Withholding formulas for resident individuals NOT claiming tax-free threshold
// Based on ATO formulas: https://www.ato.gov.au/tax-rates-and-codes/tax-table-weekly/
// Weekly earnings
const WITHHOLDING_WEEKLY_RESIDENT_NO_TFT = [
  { min: 0, max: 88, a: 0.19, b: 0 },
  { min: 88, max: 371, a: 0.19, b: 0 },
  { min: 371, max: 516, a: 0.2900, b: 37.10 },
  { min: 516, max: 721, a: 0.2100, b: -4.00 },
  { min: 721, max: 865, a: 0.2190, b: 2.48 },
  { min: 865, max: 1282, a: 0.3477, b: 113.81 },
  { min: 1282, max: 2307, a: 0.345, b: 109.84 },
  { min: 2307, max: 3461, a: 0.39, b: 213.49 },
  { min: 3461, max: 3654, a: 0.47, b: 591.73 },
  { min: 3654, max: Infinity, a: 0.45, b: 518.77 },
];

// 2024-25 Withholding formulas for foreign residents
// Weekly earnings
const WITHHOLDING_WEEKLY_NON_RESIDENT = [
  { min: 0, max: 371, a: 0.325, b: 0 },
  { min: 371, max: 515, a: 0.325, b: 0 },
  { min: 515, max: 722, a: 0.325, b: 0 },
  { min: 722, max: 865, a: 0.325, b: 0 },
  { min: 865, max: 1282, a: 0.3477, b: 19.65 },
  { min: 1282, max: 2307, a: 0.345, b: 15.68 },
  { min: 2307, max: 3461, a: 0.39, b: 119.33 },
  { min: 3461, max: 3654, a: 0.47, b: 497.57 },
  { min: 3654, max: Infinity, a: 0.45, b: 424.61 },
];

// 2024-25 Withholding formulas for working holiday makers
// Weekly earnings
const WITHHOLDING_WEEKLY_WORKING_HOLIDAY = [
  { min: 0, max: 865, a: 0.15, b: 0 },
  { min: 865, max: 1282, a: 0.3477, b: 19.65 },
  { min: 1282, max: 2307, a: 0.345, b: 15.68 },
  { min: 2307, max: 3461, a: 0.39, b: 119.33 },
  { min: 3461, max: 3654, a: 0.47, b: 497.57 },
  { min: 3654, max: Infinity, a: 0.45, b: 424.61 },
];

// Helper function to get the appropriate formula based on income, residency and TFT status
function getWeeklyWithholdingFormula(income: number, isResident: boolean, claimTaxFreeThreshold: boolean, isWorkingHoliday: boolean = false) {
  // Return the appropriate formula based on residency status and TFT
  if (isWorkingHoliday) {
    return WITHHOLDING_WEEKLY_WORKING_HOLIDAY.find(f => income >= f.min && income <= f.max);
  } else if (!isResident) {
    return WITHHOLDING_WEEKLY_NON_RESIDENT.find(f => income >= f.min && income <= f.max);
  } else if (isResident && claimTaxFreeThreshold) {
    return WITHHOLDING_WEEKLY_RESIDENT_TFT.find(f => income >= f.min && income <= f.max);
  } else {
    return WITHHOLDING_WEEKLY_RESIDENT_NO_TFT.find(f => income >= f.min && income <= f.max);
  }
}

// Calculate weekly PAYG withholding
function calculateWeeklyWithholding(weeklyIncome: number, isResident: boolean, claimTaxFreeThreshold: boolean, isWorkingHoliday: boolean = false, hasHELP: boolean = false): number {
  // Get the formula
  const formula = getWeeklyWithholdingFormula(weeklyIncome, isResident, claimTaxFreeThreshold, isWorkingHoliday);
  
  if (!formula) return 0;
  
  // Calculate the withholding: (income * a) - b
  let withholding = (weeklyIncome * formula.a) - formula.b;
  
  // HELP/HECS additional withholding (based on ATO rates)
  // Weekly HELP/HECS thresholds are approximately annual thresholds / 52
  if (hasHELP && weeklyIncome > 990) { // ~$51,550 per year
    // Apply HELP/HECS repayment rates based on income
    if (weeklyIncome > 2900) { // ~$150,800 per year (10%)
      withholding += weeklyIncome * 0.1;
    } else if (weeklyIncome > 2650) { // ~$138,000 per year (9.5%)
      withholding += weeklyIncome * 0.095;
    } else if (weeklyIncome > 2540) { // ~$132,470 per year (8.5%)
      withholding += weeklyIncome * 0.085;
    } else if (weeklyIncome > 2440) { // ~$126,940 per year (8%)
      withholding += weeklyIncome * 0.08;
    } else if (weeklyIncome > 2330) { // ~$121,410 per year (7.5%)
      withholding += weeklyIncome * 0.075;
    } else if (weeklyIncome > 2220) { // ~$115,880 per year (7%)
      withholding += weeklyIncome * 0.07;
    } else if (weeklyIncome > 2120) { // ~$110,350 per year (6.5%)
      withholding += weeklyIncome * 0.065;
    } else if (weeklyIncome > 1970) { // ~$102,660 per year (6%)
      withholding += weeklyIncome * 0.06;
    } else if (weeklyIncome > 1860) { // ~$96,960 per year (5.5%)
      withholding += weeklyIncome * 0.055;
    } else if (weeklyIncome > 1745) { // ~$90,935 per year (5%)
      withholding += weeklyIncome * 0.05;
    } else if (weeklyIncome > 1640) { // ~$84,910 per year (4.5%)
      withholding += weeklyIncome * 0.045;
    } else if (weeklyIncome > 1525) { // ~$79,210 per year (4%)
      withholding += weeklyIncome * 0.04;
    } else if (weeklyIncome > 1415) { // ~$73,510 per year (3.5%)
      withholding += weeklyIncome * 0.035;
    } else if (weeklyIncome > 1310) { // ~$68,140 per year (3%)
      withholding += weeklyIncome * 0.03;
    } else if (weeklyIncome > 1205) { // ~$62,760 per year (2.5%)
      withholding += weeklyIncome * 0.025;
    } else if (weeklyIncome > 1100) { // ~$57,150 per year (2%)
      withholding += weeklyIncome * 0.02;
    } else { // Between $51,550 and $57,150 per year (1%)
      withholding += weeklyIncome * 0.01;
    }
  }
  
  return Math.max(0, withholding);
}

// Convert between frequencies
function convertFromWeeklyWithholding(weeklyWithholding: number, frequency: FrequencyType): number {
  switch (frequency) {
    case 'fortnightly':
      return weeklyWithholding * 2;
    case 'monthly':
      return weeklyWithholding * 4.33; // Average weeks in a month
    case 'annually':
      return weeklyWithholding * 52;
    case 'weekly':
    default:
      return weeklyWithholding;
  }
}

// Convert income to weekly for withholding calculations
function convertToWeeklyIncome(income: number, frequency: FrequencyType): number {
  switch (frequency) {
    case 'fortnightly':
      return income / 2;
    case 'monthly':
      return income / 4.33; // Average weeks in a month
    case 'annually':
      return income / 52;
    case 'weekly':
    default:
      return income;
  }
}

// Calculate PAYG withholding based on frequency
export function calculatePAYGWithholding(
  income: number, 
  frequency: FrequencyType, 
  isResident: boolean, 
  claimTaxFreeThreshold: boolean,
  hasHELP: boolean = false,
  isWorkingHoliday: boolean = false
): number {
  // Convert income to weekly
  const weeklyIncome = convertToWeeklyIncome(income, frequency);
  
  // Calculate weekly withholding
  const weeklyWithholding = calculateWeeklyWithholding(
    weeklyIncome, 
    isResident, 
    claimTaxFreeThreshold,
    isWorkingHoliday,
    hasHELP
  );
  
  // Convert back to the original frequency
  const totalWithholding = convertFromWeeklyWithholding(weeklyWithholding, frequency);
  
  return round(totalWithholding);
}

// Calculate estimated tax return/owing based on actual tax obligation vs withholding
export function calculateEstimatedTaxReturn(
  actualTaxOwed: number,
  income: number,
  frequency: FrequencyType,
  isResident: boolean,
  claimTaxFreeThreshold: boolean,
  hasHELP: boolean = false,
  isWorkingHoliday: boolean = false
): number {
  // Calculate withholding based on income and frequency
  const payg = calculatePAYGWithholding(
    income,
    frequency,
    isResident,
    claimTaxFreeThreshold,
    hasHELP,
    isWorkingHoliday
  );
  
  // Convert to annual withholding if not already annual
  const annualWithholding = frequency === 'annually' ? payg : convertFromWeeklyWithholding(
    convertToWeeklyIncome(payg, frequency),
    'annually'
  );
  
  // Return value: positive means refund, negative means owing more tax
  return round(annualWithholding - actualTaxOwed);
}